/**
 * Where this app's browser code reaches the local validator.
 *
 * Deliberately NOT in `kit/`. kit holds Solana's real defaults — 8899 and 8900 —
 * and has a portability contract that keeps it copyable into an unrelated
 * project. This repo publishes its validator on different host ports because
 * another checkout of the same toolchain on the same machine already owns the
 * defaults. That is a fact about this machine and this repo, so it lives in
 * repo-specific code.
 *
 * `./stack` chooses the numbers and exports them into the frontend container;
 * see the top of that script. Both are `undefined` when the app is built
 * outside the stack, in which case kit's defaults apply — right for anyone who
 * has the validator on the standard ports.
 */
import { type Cluster, parseCluster } from './kit/index.js'

export const localnetRpcUrl: string | undefined = import.meta.env.VITE_SOLANA_RPC_URL

export const localnetWsUrl: string | undefined = import.meta.env.VITE_SOLANA_WS_URL

/**
 * Which cluster the admin UI signs against. Configuration rather than a
 * constant because the alternative is a code change to publish anywhere real,
 * and a code change is exactly the wrong thing to need at that moment.
 *
 * The endpoint overrides above only apply to localnet; devnet and mainnet use
 * kit's public URLs, which `SolanaProvider` already handles.
 */
export const solanaCluster: Cluster = parseCluster(import.meta.env.VITE_SOLANA_CLUSTER ?? 'localnet')

/** The RPC override for the configured cluster, or undefined to use kit's default. */
export const clusterRpcUrl = (): string | undefined =>
  solanaCluster === 'localnet' ? localnetRpcUrl : undefined

export const clusterWsUrl = (): string | undefined =>
  solanaCluster === 'localnet' ? localnetWsUrl : undefined
